import { FC } from 'react'

import {
  Box,
  BorderRadius6,
  Checkbox,
  Flex,
  GreyGrey10,
  GreyGrey40,
  GreyGrey60,
  GreyGrey80,
  IconClock,
  IndigoIndigo60,
  Link,
  Primary12Regular,
  Primary13Regular
} from '@sequencehq/core-components'

import { RecipientRow } from './RecipientRow'
import { QUOTE_RECIPIENTS_NOTIFY_LIST_COPY } from './QuoteRecipientsNotifyList.constants'
import {
  type EditableRecipientType,
  type QuoteRecipientRow,
  isSigningRow
} from './useQuoteRecipientRows'

type Props = {
  rows: QuoteRecipientRow[]
  selectedIds: string[]
  onSelectionChange: (selectedIds: string[]) => void
  /**
   * Gives each signer/cc row a small Signer/CC select. Without it the rows are
   * read-only and state their type as a badge beside the name.
   */
  canEditRecipientType?: boolean
  onTypeChange?: (rowId: string, type: EditableRecipientType) => void
  showCounterSignerLastNote?: boolean
  /**
   * Set while signers can't be asked to sign. Their rows stay listed but can't
   * be ticked, and this note under the list says why.
   */
  signersUnavailableNote?: string
  disabled?: boolean
  'data-testid'?: string
}

export const QuoteRecipientsNotifyList: FC<Props> = ({
  rows,
  selectedIds,
  onSelectionChange,
  canEditRecipientType = false,
  onTypeChange,
  showCounterSignerLastNote = false,
  signersUnavailableNote,
  disabled = false,
  'data-testid': dataTestId
}) => {
  const isSelectable = (row: QuoteRecipientRow) =>
    !signersUnavailableNote || !isSigningRow(row)
  const selectableRows = rows.filter(isSelectable)

  const selected = new Set(selectedIds)
  const selectedRowCount = selectableRows.filter(row =>
    selected.has(row.id)
  ).length
  const areAllSelected =
    selectableRows.length > 0 && selectedRowCount === selectableRows.length

  const toggleAll = () =>
    onSelectionChange(areAllSelected ? [] : selectableRows.map(row => row.id))

  const toggleRow = (rowId: string) =>
    onSelectionChange(
      selected.has(rowId)
        ? selectedIds.filter(id => id !== rowId)
        : [...selectedIds, rowId]
    )

  return (
    <Flex flexDir="column" gap="8px" data-testid={dataTestId}>
      <Flex
        flexDir="column"
        border={`${GreyGrey40} 1px solid`}
        borderRadius={BorderRadius6}
        overflow="hidden"
      >
        <Flex
          alignItems="center"
          padding="8px 12px"
          backgroundColor={GreyGrey10}
        >
          <Checkbox
            data-testid={dataTestId ? `${dataTestId}.all` : undefined}
            isChecked={areAllSelected}
            isIndeterminate={selectedRowCount > 0 && !areAllSelected}
            disabled={disabled || selectableRows.length === 0}
            onChange={toggleAll}
            label={
              <Box {...Primary13Regular} color={GreyGrey80}>
                {QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.allRecipients}
              </Box>
            }
          />
        </Flex>

        {rows.map(row => (
          <RecipientRow
            key={row.id}
            row={row}
            isSelected={isSelectable(row) && selected.has(row.id)}
            onToggle={() => toggleRow(row.id)}
            canEditRecipientType={canEditRecipientType}
            onTypeChange={onTypeChange}
            disabled={disabled}
            isCheckboxDisabled={!isSelectable(row)}
            data-testid={
              dataTestId ? `${dataTestId}.row.${row.email}` : undefined
            }
          />
        ))}
      </Flex>

      {signersUnavailableNote && (
        <Box
          {...Primary12Regular}
          color={GreyGrey60}
          paddingTop="4px"
          data-testid={
            dataTestId ? `${dataTestId}.signersUnavailableNote` : undefined
          }
        >
          {signersUnavailableNote}
        </Box>
      )}

      {showCounterSignerLastNote && (
        <Flex
          gap="8px"
          alignItems="center"
          paddingTop="4px"
          color={GreyGrey60}
          data-testid={
            dataTestId ? `${dataTestId}.counterSignerLastNote` : undefined
          }
        >
          <IconClock color={GreyGrey60} />
          <Box {...Primary12Regular}>
            {QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.counterSignerLastNote}{' '}
            <Link.Unstyled
              style={{ color: IndigoIndigo60 }}
              to={QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.esignatureDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {QUOTE_RECIPIENTS_NOTIFY_LIST_COPY.learnMore}
            </Link.Unstyled>
          </Box>
        </Flex>
      )}
    </Flex>
  )
}
